import dayjs from "dayjs";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { MasterIndex } from "verdi-web-client";

import { getVerdiIdFromMasterIndex } from "./verdi-id";
import { FILENAME_DATE_FORMAT, FILENAME_DATETIME_FORMAT, SOURCE_TYPE } from "../constants";

function generateDeviceStringForFilename(deviceId: string) {
    const verdiId = getVerdiIdFromMasterIndex(deviceId);
    const device =
        MasterIndex.irrigationDevice.byID[deviceId] ??
        MasterIndex.blockValve.byID[deviceId] ??
        MasterIndex.thirdPartyDevice.byID[deviceId];
    const deviceName = device?.name ?? "";

    // Get the device label if it's not the same as the ID (the EUI is set as the name in verdi-web-client which needs to be fixed)
    const deviceLabel = deviceId === deviceName ? "" : deviceName;

    // Combine the label, ID, and verdiId into a single string (ignoring empty strings)
    return [`'${deviceLabel}'`, deviceId, verdiId].filter(Boolean).join("_");
}

/**
 * Generates a filename for the exported data
 */
export function generateFileName(
    sources: { id: string; type: SOURCE_TYPE }[],
    dataTypeLabels: string[],
    startDate: Date,
    endDate: Date,
) {
    // Generate the source portion of the filename
    let sourcePortionOfFilename = "";
    // If multiple sources are selected, just use "multiple_sources"
    if (sources.length > 1) {
        sourcePortionOfFilename = `multiple_sources`;
    }
    // If a single source is selected, use the source type and ID
    else {
        const sourceType = sources[0].type;
        const sourceID = sources[0].id;

        // Get the source name
        let sourceName;
        switch (sourceType) {
            case SOURCE_TYPE.ACCOUNT:
                sourceName = `'${SessionHandler.currentUserObject?.name}'`;
                break;
            case SOURCE_TYPE.FIELD:
                sourceName = `'${MasterIndex.aoi.byID[sourceID].name}'`;
                break;
            case SOURCE_TYPE.ZONE:
                sourceName = `'${MasterIndex.zone.byID[sourceID].name}'`;
                break;
            case SOURCE_TYPE.DEVICE:
                sourceName = generateDeviceStringForFilename(sourceID);
                break;
            default:
                sourceName = `'unknown source'`;
                break;
        }

        // Generate the source portion
        sourcePortionOfFilename = `${sourceType}_${sourceName}`;
    }

    // Generate the data types portion of the filename
    let dataTypesPortionOfFilename = "";
    // If multiple data types are selected, just use "multiple_data_types"
    if (dataTypeLabels.length > 1) {
        dataTypesPortionOfFilename = `multiple_data_types`;
    }
    // If a single data type is selected, use the data type name
    else {
        dataTypesPortionOfFilename = dataTypeLabels[0];
    }

    const startDateFormatted = dayjs(startDate).format(FILENAME_DATE_FORMAT);
    const endDateFormatted = dayjs(endDate).format(FILENAME_DATE_FORMAT);
    const currentDateTimeFormatted = dayjs().format(FILENAME_DATETIME_FORMAT);

    return `verdi_export_${sourcePortionOfFilename}_'${dataTypesPortionOfFilename}'_${startDateFormatted}_to_${endDateFormatted}_at_${currentDateTimeFormatted}.csv`;
}
